import { defineStore } from 'pinia';
import { fetchWrapper } from '../helpers/FetchWrapper';
import { useAlertStore } from './Alert';
import { User } from '../interfaces/User.interface'
import { BaseResponse } from '../interfaces/BaseResponse.interface'
import { Profile } from '../interfaces/Profile.interface';

const baseUrl: string = `${import.meta.env.VITE_API_URL}/security`;

function applyAuthenticatedUser(store: any, user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', user.token);
    localStorage.setItem('refreshToken', user.refreshToken);
    localStorage.setItem('tokenExpiresAt', user.expiresAt);
    store.isAuthenticated = true;
    store.avatar = user.avatar;
    store.preferredLanguage = user.preferredLanguage;
    store.theme = user.theme;
    store.userrole = user.role;
    store.isModerator = user.isModerator;
    store.ipFromBrazil = user.ipFromBrazil;
    store.name = user.name;
    store.userName = user.userName;
    store.email = user.email;
    store.redirect = user.redirect;
}

export const useAuthStore = defineStore('Auth', {
    state: (): {
        returnUrl: string | null; isAuthenticated: boolean | null; avatar: string | null; preferredLanguage: string | null;
        theme: string | null;
        userrole: string | null;
        isModerator: boolean | null;
        ipFromBrazil: boolean | null;
        name: string | null;
        userName: string | null;
        email: string | null;
        redirect: string | null;
    } => ({
        returnUrl: null,
        isAuthenticated: false,
        avatar: null,
        preferredLanguage: null,
        theme: null,
        userrole: null,
        isModerator: null,
        ipFromBrazil: null,
        name: null,
        userName: null,
        email: null,
        redirect: null,
    }),
    actions: {
        login: async function (this: any, userkey: string, password: string): Promise<BaseResponse<boolean>> {
            try {
                let response = await fetchWrapper.post(`${baseUrl}/login`, { userkey: userkey, password });
                const user: User = response.value;

                if (response.success) {
                    // store user details and jwt in local storage to keep user logged in between page refreshes
                    console.log('Login User:', user);
                    applyAuthenticatedUser(this, user);
                }
                return response;

            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<boolean>(false, `${error}`, false);
            }
        },
        logout: async function (this: any): Promise<void> {
            console.log('Logout User');
            let token = localStorage.getItem('token');
            let refreshToken= localStorage.getItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            this.isAuthenticated = false;
            this.avatar = null;
            this.preferredLanguage = null;
            this.theme = null;
            this.userrole = null;
            this.isModerator = null;
            this.name = null;
            this.userName = null;
            this.email = null;
            this.ipFromBrazil = null;
            this.redirect = null;
            let response = await fetchWrapper.post(`${baseUrl}/Logoff`, { token: token, refreshToken: refreshToken });
            if (response.success) {
                console.log('Logoff successful');
            }
            //router.push('/account/login');
        },
        userinfo: async function (this: any): Promise<void> {
            try {
                let response = await fetchWrapper.get(`${baseUrl}/userinfo`);
                const user: User = response.value;
                console.log(user);
                if (user && response.success) {
                    this.isAuthenticated = true;
                    this.avatar = user.avatar;
                    this.preferredLanguage = user.preferredLanguage;
                    this.theme = user.theme;
                    this.userrole = user.role;
                    this.isModerator = user.isModerator;
                    this.name = user.name;
                    this.userName = user.userName;
                    this.email = user.email;
                    this.ipFromBrazil = user.ipFromBrazil;
                    this.redirect = user.redirect;
                    localStorage.setItem('user', JSON.stringify(user));
                }
                else
                {
                    console.log("userinfo failed");
                     this.isAuthenticated = false;
                }
            } catch (error) {
                console.log("erro-userinfo", error);
            }
        },
        getProfile: async function (this: any): Promise<BaseResponse<Profile>> {
            try {
                let response = await fetchWrapper.get(`${baseUrl}/getprofile`);
                return response;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<Profile>(false, `${error}`, {} as Profile);
            }
        },
        confirmEmail: async function (this: any, token: string): Promise<BaseResponse<boolean>> {
            try {
                let response = await fetchWrapper.get(`${baseUrl}/ConfirmEmail?token=${token}`);
                return response;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<boolean>(false, `${error}`, false);
            }
        },
        generateRecovery: async function (this: any, email: string): Promise<BaseResponse<boolean>> {
            try {
                let response = await fetchWrapper.get(`${baseUrl}/GenerateRecovery?email=${encodeURIComponent(email)}`);
                return response;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<boolean>(false, `${error}`, false);
            }
        },
        validateRecovery: async function (this: any, recoveryKey: string): Promise<BaseResponse<boolean>> {
            try {
                let response = await fetchWrapper.get(`${baseUrl}/ValidateRecovery?recoverykey=${encodeURIComponent(recoveryKey)}`);
                return response;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<boolean>(false, `${error}`, false);
            }
        },
        applyRecovery: async function (this: any, recoveryKey: string, newPassword: string): Promise<BaseResponse<boolean>> {
            try {
                let response = await fetchWrapper.post(`${baseUrl}/ApplyRecovery`, { recoveryKey, newPassword });
                return response;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<boolean>(false, `${error}`, false);
            }
        },
        changePassword: async function (this: any, currentPassword: string, newPassword: string): Promise<BaseResponse<boolean>> {
            try {
                let response = await fetchWrapper.post(`${baseUrl}/ChangePassword`, {
                    currentPassword,
                    newPassword
                });
                return response;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<boolean>(false, `${error}`, false);
            }
        },
        generate2FACode: async function (this: any): Promise<BaseResponse<string>> {
            try {
                let response = await fetchWrapper.post(`${baseUrl}/Generate2FACode`);
                return response;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<string>(false, `${error}`, '');
            }
        },
        generateOtp2FAQrCode: async function (this: any): Promise<BaseResponse<string>> {
            try {
                let response = await fetchWrapper.post(`${baseUrl}/GenerateOtp2FAQrCode`);
                return response;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<string>(false, `${error}`, '');
            }
        },
        validate2FACode: async function (this: any, code: string): Promise<BaseResponse<boolean>> {
            try {
                let response = await fetchWrapper.get(`${baseUrl}/Validate2FACode?code=${code}`);
                // After login the token is temporary (2FA pending). A valid code returns the real one; swap it in.
                const fullToken = response.value?.token;
                if (response.success && fullToken) {
                    localStorage.setItem('token', fullToken);
                }
                return response;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<boolean>(false, `${error}`, false);
            }
        },
        getOtp2FAQrCode: async function (this: any): Promise<BaseResponse<string>> {
            try {
                let result: BaseResponse<string> = { success: false, value: '' };
                let token = localStorage.getItem('token');
                const response = await fetch(`${baseUrl}/GetOtp2FAQrCode`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.ok) {
                    result.success = true;
                    result.value = await response.text();
                    console.log('QrCode:', result.value);
                } else {
                    result.message = 'Failed to fetch QR code';
                }
                return result;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<string>(false, `${error}`, '');
            }
        },
        // Asks the backend for the provider's OAuth authorize URL (JSON, not a redirect). The caller
        // must then navigate the browser there itself (window.location.href) — same generic method
        // for any provider (google, github, ...). The final return destination is decided by the
        // backend's own config, not sent from here. subscriptionPlanId is only needed when this
        // might register a brand-new user (sign-up); omit it for a plain sign-in.
        getExternalLoginUrl: async function (provider: string, subscriptionPlanId?: string): Promise<BaseResponse<string>> {
            try {
                const query = subscriptionPlanId ? `?subscriptionPlanId=${encodeURIComponent(subscriptionPlanId)}` : '';
                let response = await fetchWrapper.get(`${baseUrl}/ExternalLogin/${provider}/AuthorizeUrl${query}`);
                return { success: response.success, message: response.message, value: response.value?.url };
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<string>(false, `${error}`, '');
            }
        },
        externalLoginCallback: async function (this: any, provider: string, loginCode: string): Promise<BaseResponse<boolean>> {
            try {
                let response = await fetchWrapper.post(`${baseUrl}/ExternalLogin/${provider}/Exchange`, { loginCode });
                const user: User = response.value;

                if (response.success) {
                    console.log('External Login User:', user);
                    applyAuthenticatedUser(this, user);
                }
                return response;
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<boolean>(false, `${error}`, false);
            }
        },
        // Starts a Stripe Checkout session for a plan. Returns the hosted checkout URL — the caller
        // must navigate the browser there itself (window.location.href), not fetch it as a redirect.
        // Without a plan id the backend checks out the logged user's current plan (waiting-payment screen).
        startCheckout: async function (subscriptionPlanId?: string): Promise<BaseResponse<string>> {
            try {
                let response = await fetchWrapper.post(`${baseUrl}/payments/checkout`, subscriptionPlanId ? { subscriptionPlanId } : {});
                return { success: response.success, message: response.message, value: response.value?.url };
            } catch (error) {
                console.log(error);
                const alertStore = useAlertStore();
                alertStore.error(`${error}`);
                return new BaseResponse<string>(false, `${error}`, '');
            }
        }
    }
});

